import { canauxTarifes } from '../canaux'
import { UNITES, libelleUnite } from './wizardConstants'

/**
 * Etape 3 (parcours A) : « Vos prix » — prototype v7.4, WizardOffre/prix.
 *
 * REFAIT AU BANC D'ESSAI VISUEL (S68). L'ancien ecran empilait : rappel du
 * produit, bascule de mode en degrade, gros champ blanc a bordure orange,
 * ligne « Tu touches… » (un tutoiement rescape), selecteur d'unite, puis le
 * choix du canal en pastilles tout en bas. Le prototype fait l'inverse : UNE
 * CARTE PAR CANAL ACTIF, chacune avec son taux, ses deux modes, son champ, et
 * un bloc de transparence en quatre lignes — ce que je touche, la commission,
 * la TVA sur la commission, ce que paie le client. Le producteur voit le
 * partage exact avant de valider.
 *
 * Le calcul suit ADR-058 §5 (precision d'Enzo du 3 juillet) et la
 * Constitution (art. 2 : prix_final = prix_producteur / (1 - commission)) :
 * la commission est un pourcentage du PRIX PAYE PAR LE CLIENT, et la TVA 8,5 %
 * sur la commission est COMPRISE DEDANS. Le client paie donc exactement le prix
 * de vente stocke — c'est ce que montrent « Ma page », la fiche et le stand, et
 * c'est ce que regle le producteur au marche (du = CA x taux, TTC).
 * S75 : le prototype ajoutait la TVA PAR-DESSUS (« le client paie 5,00 € »
 * pour un prix affiche de 4,96 €) — incoherence remontee par une productrice.
 *
 * L'UNITE DE VENTE se choisit ici (S71, retour Enzo : « la seule unite que
 * vous proposez c'est le kilo, alors que c'est vendu a la piece »). Le
 * prototype la fixe a l'etape « proposer » pour un produit nouveau ; pour un
 * produit du referentiel, l'unite du referentiel n'est qu'un DEFAUT — c'est
 * l'offre qui porte son unite (ADR-070). Elle vit avec le prix parce que le
 * prix en depend : « 3 € / kg » et « 3 € / piece » ne disent pas la meme chose.
 * L'Atelier n'est pas tarife (« tarification a venir avec le module
 * Transformation », prototype).
 */

const TVA_COMMISSION = 0.085
const centimes = (n) => Math.round((n + Number.EPSILON) * 100) / 100

/**
 * Decomposition d'un prix de vente client `pv` : commission = taux x pv, TVA
 * comprise ; `tva` n'est que la part de TVA DANS la commission (« dont »).
 * net + com = client = pv, au centime.
 */
const decomposer = (pv, net) => {
  const com = centimes(pv - net)
  const tva = centimes(com - com / (1 + TVA_COMMISSION))
  return { net, pv, com, tva, client: pv }
}
export const calcDepuisNet = (net, taux) => decomposer(centimes(net / (1 - taux)), net)
export const calcDepuisClient = (client, taux) => decomposer(centimes(client), centimes(client * (1 - taux)))

export const fmtEuros = (n) => `${(Math.round(n * 100) / 100).toFixed(2).replace('.', ',')} €`

function StepPrice({ product, catalogue, canaux, prix, onPrixChange, unite, onUniteChange }) {
  const tarifes = canauxTarifes(catalogue, canaux)
  const uniteRef = product?.unit || 'kg'
  const uniteEff = unite || uniteRef
  // Un produit propose vient de choisir son unite (ecran « Vous le vendez… ») :
  // on ne la redemande pas. Un produit du referentiel peut la changer ici.
  const choixUnite = !product?.proposition && !!onUniteChange
  // Les unites connues, plus celle du referentiel si elle est exotique.
  const unites = UNITES.some((u) => u.value === uniteRef)
    ? UNITES
    : [{ value: uniteRef, label: uniteRef }, ...UNITES]

  const blocUnite = choixUnite && (
    <div className="panneau-verre rounded-[20px] px-4 py-3.5 mb-3.5">
      <p className="text-[12px] font-extrabold text-ink mb-2.5">Vous le vendez…</p>
      <div className="flex flex-wrap gap-[7px]">
        {unites.map((u) => {
          const actif = u.value === uniteEff
          return (
            <button
              key={u.value}
              onClick={() => onUniteChange(u.value)}
              className="rounded-full px-[13px] py-[7px] text-[12px] font-bold transition-colors"
              style={actif
                ? { background: 'rgb(var(--c-green))', color: 'rgb(var(--c-creme))' }
                : { background: 'rgba(255,255,255,0.6)', color: 'rgba(7,62,61,0.6)' }}
            >
              {u.value === uniteRef ? `${u.label} · référentiel` : u.label}
            </button>
          )
        })}
      </div>
    </div>
  )

  if (tarifes.length === 0) {
    return (
      <div>
        {blocUnite}
        <p className="text-[13px] text-ink/60">
          Aucun canal tarifé (Atelier : tarification à venir avec le module Transformation). Continuez.
        </p>
      </div>
    )
  }

  return (
    <div>
      {blocUnite}
      {tarifes.map((ch) => {
        const taux = Number(ch.module?.commissionDefaut ?? 10) / 100
        const pr = prix[ch.code] || { mode: 'net', val: '' }
        const val = parseFloat((pr.val || '').replace(',', '.'))
        const calc = !isNaN(val) && val > 0
          ? (pr.mode === 'net' ? calcDepuisNet(val, taux) : calcDepuisClient(val, taux))
          : null
        const setPr = (patch) => onPrixChange(ch.code, { ...pr, ...patch })

        return (
          <div key={ch.code} className="panneau-verre rounded-[20px] px-4 py-3.5 mb-3.5">
            <p className="text-[12px] font-extrabold mb-2.5" style={{ color: ch.c }}>
              {ch.e} {ch.l} · commission {Math.round(taux * 100)} %
            </p>

            {/* Deux modes de saisie */}
            <div className="flex gap-1.5 mb-2.5">
              {[['net', 'Ce que je veux toucher'], ['client', 'Mon prix de vente']].map(([m, l]) => (
                <button
                  key={m}
                  onClick={() => setPr({ mode: m })}
                  className="flex-1 rounded-xl px-1 py-[9px] text-[11px] font-bold whitespace-nowrap transition-colors"
                  style={pr.mode === m
                    ? { background: 'rgb(var(--c-green))', color: 'rgb(var(--c-creme))' }
                    : { background: 'rgba(7,62,61,0.06)', color: 'rgba(7,62,61,0.6)' }}
                >
                  {l}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input
                value={pr.val}
                inputMode="decimal"
                onChange={(e) => setPr({ val: e.target.value })}
                placeholder="0,00"
                className="flex-1 min-w-0 rounded-[14px] px-3.5 py-3 text-base font-serif text-ink outline-none"
                style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(7,62,61,0.12)' }}
              />
              <span className="text-[13px] text-ink/60 flex-shrink-0">€ / {libelleUnite(uniteEff)}</span>
            </div>

            {/* Transparence : quatre lignes, pas une de moins */}
            {calc && (
              <div className="mt-3 rounded-[14px] px-[13px] py-[11px]" style={{ background: 'rgba(7,62,61,0.04)' }}>
                {[
                  ['Vous touchez (net)', calc.net, true],
                  [`Commission Manrina (${Math.round(taux * 100)} %, TVA comprise)`, calc.com, false],
                  ['dont TVA 8,5 % sur la commission', calc.tva, false],
                  ['Le client paie', calc.client, true],
                ].map(([l, v, fort]) => (
                  <div key={l} className="flex justify-between py-[2.5px]">
                    <span className={`text-[12px] ${fort ? 'font-bold text-ink' : 'font-medium text-ink/65'}`}>{l}</span>
                    <span className={`font-serif ${fort ? 'text-[14.5px] font-bold text-ink' : 'text-[13px] text-ink/70'}`}>{fmtEuros(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default StepPrice
